import axios, { AxiosError, type AxiosInstance } from 'axios'

import { useAuthStore } from '@/lib/auth/store'

/**
 * Shared API client.
 * Interceptors:
 *   1. attach the JWT
 *   2. attach a request id
 *   3. on 401, silently refresh the access token and retry once; only if
 *      that fails do we clear the session and redirect to /login
 */
// Step-up auth endpoints re-challenge the user for a credential (password,
// TOTP code) on top of an existing session. A 401 from these means "wrong
// credential for this elevated action", not "your session is gone" — so the
// global session-expiry handler must skip them.
function isStepUpEndpoint(url: string | undefined): boolean {
  if (!url) return false
  return url.endsWith('/v1/auth/totp/start') || url.endsWith('/v1/auth/totp/confirm')
}

// The refresh + login endpoints must never be run through the refresh-retry
// path — a 401 from them is terminal, not a "your access token aged out" cue.
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false
  return url.endsWith('/v1/auth/refresh') || url.endsWith('/v1/auth/login')
}

export function createApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 30_000,
    headers: { Accept: 'application/json' },
  })

  client.interceptors.request.use((config) => {
    const auth = useAuthStore.getState()
    if (auth.accessToken) {
      config.headers.Authorization = `Bearer ${auth.accessToken}`
    }
    config.headers['X-Request-Id'] = crypto.randomUUID()
    return config
  })

  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const original = error.config as
        | (typeof error.config & { _retried?: boolean })
        | undefined

      if (
        error.response?.status === 401 &&
        !isStepUpEndpoint(original?.url) &&
        !isAuthEndpoint(original?.url) &&
        !original?._retried
      ) {
        const state = useAuthStore.getState()

        // The access token likely just aged out. Try a single silent
        // refresh; if it yields a fresh token, replay the original request
        // once with it. This is what keeps the session seamless — the user
        // never sees the 15-30 min access-token boundary.
        if (state.refreshToken) {
          const newAccess = await state.refresh()
          if (newAccess && original) {
            original._retried = true
            original.headers = original.headers ?? {}
            original.headers.Authorization = `Bearer ${newAccess}`
            return client(original)
          }
          // Refresh failed but the session survived it (network blip, API
          // restarting): fail this request only, and try again next time.
          if (useAuthStore.getState().refreshToken) return Promise.reject(error)
        }

        // No refresh token, or the refresh was refused → the session is over.
        // Clear it (marking session_expired so LoginPage explains why) and
        // force a clean redirect. Just clearing the store isn't enough:
        // TanStack Router's beforeLoad only re-runs on navigation, so a user
        // sitting on /dashboard would otherwise be stuck on a half-broken
        // page. window.location guarantees a fresh load into /login.
        const hadSession = !!state.accessToken || !!state.refreshToken
        state.logout('session_expired')
        if (
          hadSession &&
          typeof window !== 'undefined' &&
          window.location.pathname !== '/login'
        ) {
          window.location.replace('/login')
        }
      }
      return Promise.reject(error)
    },
  )

  return client
}

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8080'

export const api = createApiClient(API_BASE_URL)

/**
 * An uploaded logo comes back as an API path ("/v1/branding/…"); make it
 * loadable by an <img> wherever the SPA is served from. Absolute URLs pass
 * through unchanged.
 */
export function resolveLogoUrl(url: string | null | undefined): string | null {
  if (!url) return null
  if (!url.startsWith('/')) return url
  return API_BASE_URL.replace(/\/+$/, '') + url
}

// --- Shared API types (will move to generated/ once openapi-typescript runs) ---

export interface LoginRequest {
  tenantSlug: string
  empId: string
  password: string
  totpCode?: string | null
}

export interface LoginResponse {
  accessToken: string
  /** Null when totpPending — no refresh token until enrolment clears the gate. */
  refreshToken: string | null
  deviceTrustToken: string | null
  empId: string
  tenantSlug: string
  passwordResetRequired: boolean
  /** True → credentials were valid but TOTP must be enrolled before this
   *  session is fully authenticated. accessToken is a short-lived,
   *  scope-limited interim token — LoginPage routes to /auth/totp-setup. */
  totpPending: boolean
}

export interface LoginPrecheckRequest {
  tenantSlug: string
  empId: string
}

export interface LoginPrecheckResponse {
  /** Whether to show the TOTP code field alongside password. False for an
   *  unknown/inactive/not-yet-enrolled account — identical to a real
   *  account that doesn't need TOTP, so this can't be used to enumerate
   *  empIds beyond "this one is TOTP-enrolled and required". */
  showTotpField: boolean
}

export interface PrincipalResponse {
  userId: string
  empId: string
  tenantId: string
  tenantSlug: string
  roles: string[]
  tokenType: string
  totpEnrolled: boolean
  totpRequired: boolean
  /** Resolved tenant branding. primaryColor is non-null (backend defaults
   *  to "#204887"); logoUrl is null when no logo is set. */
  branding?: { primaryColor: string; logoUrl: string | null }
  /** Every permission code the user holds at any scope, sorted. Drives which
   *  nav entries render. A visibility hint only — the server still enforces
   *  each endpoint, so a missing entry here never grants access. */
  permissions?: string[]
  /** GRAVITON (LMS-fed) or EXCEL (file uploads). Absent from an older API. */
  tenantTier?: 'GRAVITON' | 'EXCEL'
  /** The tenant's name for display; the slug is an identifier. Absent from an older API. */
  tenantName?: string | null
  /** Display name; null if the user row is gone. */
  fullName?: string | null
  /** Nearest branch over the user's org node; null when placed above branch level or not placed. */
  branch?: { code: string; name: string } | null
}

export interface ApiError {
  code?: string
  message?: string
}

/** 403 body for a per-account lockout. The two timing fields are absent from an older API. */
export interface AccountLockedBody extends ApiError {
  code: 'account_locked'
  retryAfterSeconds?: number
  /** ISO instant. Prefer retryAfterSeconds: a device clock may be skewed. */
  lockedUntil?: string
}
